import { useState, type FormEvent } from "react";

import { AccessCell } from "../components/AccessCell.js";
import { groups as groupsApi, users, type TenantRole, type TenantUser } from "../lib/api.js";
import { relativeTime } from "../lib/freshness.js";
import { useAuth } from "../lib/auth-context.js";
import { useLiveData } from "../lib/use-live-data.js";
import { useNow } from "../lib/use-now.js";

/**
 * Tenant user administration.
 *
 * Accounts are created with a temporary password shown exactly once. It is
 * never stored in plaintext and no endpoint returns it again, so the UI has
 * to treat that one render as the only chance to copy it -- hence the
 * dismissable panel rather than a toast that can vanish before it is read.
 */

const REFRESH_MS = 60_000;

const ROLE_HELP: Record<TenantRole, string> = {
  owner: "Full control, including other owners",
  admin: "Manage devices and users; cannot change owners",
  viewer: "Read everything",
  member: "Only what's granted per group",
};

export function UsersPage() {
  const { user: me } = useAuth();
  const now = useNow();
  // An admin action should show its result immediately rather than at the
  // next tick, which is what `reload` is for.
  const { data: list, error, reload } = useLiveData(() => users.list(), REFRESH_MS);
  // The tree, for the access panel's group picker and its paths. Fetched once
  // per refresh rather than per open panel, since every row would want it.
  const { data: tree } = useLiveData(() => groupsApi.list(), REFRESH_MS);
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);
  const [addedExisting, setAddedExisting] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const run = async (id: string, action: () => Promise<unknown>) => {
    setBusy(id);
    setActionError(null);
    try {
      await action();
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setBusy(null);
    }
  };

  const canManage = list?.canManage ?? false;

  return (
    <div>
      <h1 style={{ fontSize: 15, margin: "0 0 16px", letterSpacing: "0.14em" }}>&gt; USERS</h1>

      {issued && (
        <div
          style={{
            border: "1px solid var(--status-warning)",
            background: "color-mix(in srgb, var(--status-warning) 12%, var(--surface-1))",
            padding: "12px 14px",
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          <div style={{ color: "var(--status-warning)", letterSpacing: "0.1em", marginBottom: 6 }}>
            TEMPORARY PASSWORD FOR {issued.email.toUpperCase()}
          </div>
          <code
            style={{
              display: "block",
              fontSize: 16,
              padding: "8px 10px",
              background: "var(--page-plane)",
              border: "1px solid var(--border)",
              userSelect: "all",
              wordBreak: "break-all",
            }}
          >
            {issued.password}
          </code>
          <p style={{ color: "var(--text-muted)", fontSize: 11, margin: "8px 0 0", lineHeight: 1.5 }}>
            Shown once only. Send it separately from their email address; they'll replace it on first sign-in. Lost it? Use RESET.
          </p>
          <button
            onClick={() => { setIssued(null); setAddedExisting(null); }}
            style={{ ...smallButton, marginTop: 10 }}
          >
            [ DISMISS ]
          </button>
        </div>
      )}

      {/* Adding an account that already exists issues no password -- saying so
          explicitly, because the absence of the usual panel otherwise reads
          as the action having failed. */}
      {addedExisting && (
        <p
          style={{
            color: "var(--success-text)",
            fontSize: 13,
            border: "1px solid var(--border)",
            padding: "10px 12px",
            marginTop: 0,
          }}
        >
          {`${addedExisting} already had an account and has been added. They keep their password.`}
        </p>
      )}

      {canManage && (
        <AddUserForm
          onCreated={(result) => {
            if (result.password) setIssued({ email: result.email, password: result.password });
            else setAddedExisting(result.email);
            reload();
          }}
        />
      )}

      {actionError && <p style={{ color: "var(--status-critical)", fontSize: 13 }}>{`!! ${actionError}`}</p>}
      {error && !list && <p style={{ color: "var(--status-critical)" }}>{`!! ${error}`}</p>}
      {!list && !error && <p style={{ color: "var(--text-muted)" }}>LOADING...</p>}

      {list && (
        <div style={{ border: "1px solid var(--border)", background: "var(--surface-1)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)" }}>
                {["EMAIL", "NAME", "ROLE", ...(canManage ? ["ACCESS"] : []), "STATE", "LAST SEEN", ...(canManage ? [""] : [])].map((h, i) => (
                  <th
                    key={h || `actions-${i}`}
                    style={{ padding: "10px 14px", color: "var(--text-muted)", fontWeight: 500 }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.users.map((u) => (
                <tr key={u.id} style={{ borderBottom: "1px solid var(--gridline)", opacity: u.is_active ? 1 : 0.55 }}>
                  <td style={{ padding: "10px 14px" }}>
                    {u.email}
                    {u.id === me?.id && (
                      <span style={{ color: "var(--text-muted)", fontSize: 11 }}> (you)</span>
                    )}
                  </td>
                  <td style={{ padding: "10px 14px", color: "var(--text-secondary)" }}>{u.name ?? "—"}</td>
                  <td style={{ padding: "10px 14px" }}>
                    {canManage && u.id !== me?.id ? (
                      <select
                        value={u.role}
                        disabled={busy === u.id}
                        title={ROLE_HELP[u.role]}
                        onChange={(e) =>
                          void run(u.id, () => users.update(u.id, { role: e.target.value as TenantRole }))
                        }
                        style={selectStyle}
                      >
                        {(Object.keys(ROLE_HELP) as TenantRole[]).map((role) => (
                          <option key={role} value={role}>
                            {role.toUpperCase()}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ color: "var(--text-secondary)" }} title={ROLE_HELP[u.role]}>
                        {u.role.toUpperCase()}
                      </span>
                    )}
                  </td>
                  {canManage && (
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                      <AccessCell user={u} isSelf={u.id === me?.id} groups={tree?.groups ?? []} onChange={reload} />
                    </td>
                  )}
                  <td style={{ padding: "10px 14px", fontSize: 12 }}>
                    <UserState user={u} />
                  </td>
                  <td style={{ padding: "10px 14px", color: "var(--text-muted)", fontSize: 12 }}>
                    {relativeTime(u.last_seen_at, now)}
                  </td>
                  {canManage && (
                    <td style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                      {/* Acting on yourself is blocked server-side too; hiding
                          the buttons just avoids offering a dead end. */}
                      {u.id !== me?.id && (
                        <>
                          <button
                            disabled={busy === u.id}
                            onClick={() => {
                              if (!confirm(`Reset the password for ${u.email}? Their current one stops working and they're signed out.`)) return;
                              void run(u.id, async () => {
                                const result = await users.resetPassword(u.id);
                                setIssued({ email: u.email, password: result.temporary_password });
                              });
                            }}
                            style={smallButton}
                          >
                            [ RESET ]
                          </button>{" "}
                          <button
                            disabled={busy === u.id}
                            onClick={() => void run(u.id, () => users.update(u.id, { is_active: !u.is_active }))}
                            style={smallButton}
                          >
                            {u.is_active ? "[ DISABLE ]" : "[ ENABLE ]"}
                          </button>{" "}
                          <button
                            disabled={busy === u.id}
                            onClick={() => {
                              if (!confirm(`Remove ${u.email} from this organization? Their account is kept.`)) return;
                              void run(u.id, () => users.remove(u.id));
                            }}
                            style={{ ...smallButton, color: "var(--status-critical)" }}
                          >
                            [ REMOVE ]
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** The states worth distinguishing at a glance, in priority order. */
function UserState({ user }: { user: TenantUser }) {
  if (!user.is_active) return <span style={{ color: "var(--status-critical)" }}>DISABLED</span>;
  if (user.must_change_password) {
    return <span style={{ color: "var(--status-warning)" }}>TEMP PASSWORD</span>;
  }
  if (user.has_google && !user.has_password) {
    return <span style={{ color: "var(--text-secondary)" }}>GOOGLE</span>;
  }
  return <span style={{ color: "var(--status-good)" }}>ACTIVE</span>;
}

function AddUserForm({
  onCreated,
}: {
  onCreated: (result: { email: string; password: string | null }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<TenantRole>("viewer");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const result = await users.create({ email, name: name || null, role });
      onCreated({ email, password: result.temporary_password ?? null });
      setEmail("");
      setName("");
      setRole("viewer");
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create user");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={{ ...smallButton, marginBottom: 16, padding: "6px 12px" }}>
        [ + ADD USER ]
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        border: "1px solid var(--border)",
        background: "var(--surface-1)",
        padding: 14,
        marginBottom: 16,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        alignItems: "flex-end",
      }}
    >
      <div>
        <label style={labelStyle}>&gt; EMAIL</label>
        <input
          type="email"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{ ...inputStyle, width: 240 }}
        />
      </div>
      <div>
        <label style={labelStyle}>&gt; NAME</label>
        <input value={name} onChange={(e) => setName(e.target.value)} style={{ ...inputStyle, width: 180 }} />
      </div>
      <div>
        <label style={labelStyle}>&gt; ROLE</label>
        <select value={role} onChange={(e) => setRole(e.target.value as TenantRole)} style={selectStyle}>
          {(Object.keys(ROLE_HELP) as TenantRole[]).map((r) => (
            <option key={r} value={r}>
              {r.toUpperCase()}
            </option>
          ))}
        </select>
      </div>
      <button type="submit" disabled={saving} style={{ ...smallButton, padding: "8px 14px" }}>
        {saving ? "[ CREATING... ]" : "[ CREATE ]"}
      </button>
      <button type="button" onClick={() => setOpen(false)} style={{ ...smallButton, padding: "8px 14px" }}>
        [ CANCEL ]
      </button>
      {error && <p style={{ color: "var(--status-critical)", fontSize: 13, width: "100%", margin: 0 }}>{`!! ${error}`}</p>}
      <p style={{ color: "var(--text-muted)", fontSize: 11, width: "100%", margin: 0, lineHeight: 1.5 }}>
        New accounts get a temporary password, shown once. Existing accounts are just added.
      </p>
    </form>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "7px 9px",
  fontSize: 13,
  border: "1px solid var(--border)",
  background: "var(--surface-raised)",
  color: "var(--text-primary)",
};

const selectStyle: React.CSSProperties = {
  padding: "6px 8px",
  fontSize: 12,
  border: "1px solid var(--border)",
  background: "var(--surface-raised)",
  color: "var(--text-primary)",
  letterSpacing: "0.06em",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  color: "var(--text-secondary)",
  marginBottom: 4,
  letterSpacing: "0.08em",
};

const smallButton: React.CSSProperties = {
  background: "none",
  border: "1px solid var(--border)",
  padding: "5px 9px",
  fontSize: 11,
  color: "var(--text-secondary)",
  cursor: "pointer",
  letterSpacing: "0.08em",
};
